import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Papa from 'papaparse';
import * as React from 'react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

import { SWLoadingButton } from '../../../components/LoadingButton';
import { SWLoadingButtonOutlined } from '../../../components/SWLoadingButtonOutlined';
import type { AppfolioConfig, PmsType } from '@/Structures/types';
import type { Unit } from '@/Units/types';
import { updateUnitPmsConfig } from '@/Units/api';
import { naturalCompare } from '@/util';

import { autoMatchUnits, useBillableUnits } from './pmsUtils';
import type { AppfolioRow } from './providers/appfolio';
import {
  buildAppfolioStructureConfig,
  buildAppfolioUnitConfig,
  getDistinctAppfolioPropertyNames,
  parseAppfolioRows,
} from './providers/appfolio';

interface AppfolioMatchingDialogProps {
  open: boolean;
  onClose: () => void;
  units: Unit[];
  onUpdate: (pmsType: PmsType | null, pmsConfig: AppfolioConfig | null) => Promise<void>;
  onRefresh?: () => void;
  existingConfig?: AppfolioConfig | null;
  onSaveSuccess?: () => void;
}

export const AppfolioMatchingDialog = ({
  open,
  onClose,
  units,
  onUpdate,
  onRefresh,
  existingConfig,
  onSaveSuccess,
}: AppfolioMatchingDialogProps): JSX.Element => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [parsedRows, setParsedRows] = React.useState<AppfolioRow[]>([]);
  const [parsing, setParsing] = React.useState(false);
  const [fileName, setFileName] = React.useState<string>('');

  const [selectedPropertyName, setSelectedPropertyName] = React.useState<string | null>(null);
  const [unitMatches, setUnitMatches] = React.useState<Record<string, AppfolioRow | null>>({});
  // Unit IDs whose current match came from the fuzzy pass; rendered as a chip in the
  // table. Cleared per-unit when the user manually changes that row.
  const [fuzzyMatchedUnitIds, setFuzzyMatchedUnitIds] = React.useState<Set<string>>(
    () => new Set(),
  );
  const [isSaving, setIsSaving] = React.useState(false);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const billableUnits = useBillableUnits(units);

  const distinctPropertyNames = React.useMemo(
    () => getDistinctAppfolioPropertyNames(parsedRows).sort(naturalCompare),
    [parsedRows],
  );

  const rowsForSelectedProperty = React.useMemo(
    () => parsedRows.filter((r) => r.propertyName === selectedPropertyName),
    [parsedRows, selectedPropertyName],
  );

  // Selected AppFolio rows (to filter out duplicates from the picker)
  const selectedRowIds = React.useMemo(() => {
    const ids = new Set<string>();
    Object.values(unitMatches).forEach((r) => {
      if (r) ids.add(r.unitId);
    });
    return ids;
  }, [unitMatches]);

  // Reset on close
  React.useEffect(() => {
    if (!open) {
      setParsedRows([]);
      setParsing(false);
      setFileName('');
      setSelectedPropertyName(null);
      setUnitMatches({});
      setFuzzyMatchedUnitIds(new Set());
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [open]);

  // Auto-select the only property; if existingConfig.property_name appears, prefer that
  React.useEffect(() => {
    if (distinctPropertyNames.length === 0) {
      setSelectedPropertyName(null);
      return;
    }
    if (distinctPropertyNames.length === 1) {
      setSelectedPropertyName(distinctPropertyNames[0]);
      return;
    }
    if (
      existingConfig?.property_name &&
      distinctPropertyNames.includes(existingConfig.property_name)
    ) {
      setSelectedPropertyName(existingConfig.property_name);
    }
  }, [distinctPropertyNames, existingConfig]);

  // Auto-match when rows-for-property changes
  React.useEffect(() => {
    if (rowsForSelectedProperty.length === 0) {
      setUnitMatches({});
      setFuzzyMatchedUnitIds(new Set());
      return;
    }
    autoMatch(rowsForSelectedProperty, /* showToast */ false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsForSelectedProperty, billableUnits]);

  function autoMatch(rows: AppfolioRow[], showToast: boolean) {
    const {
      matches,
      count,
      total,
      fuzzyMatchedUnitIds: fuzzyIds,
    } = autoMatchUnits(billableUnits, rows, {
      getRowId: (r) => r.unitId,
      getRowName: (r) => r.unitName,
      getUnitPersistedId: (u) => u.pms_config?.unit_id,
      getUnitName: (u) => u.pms_config?.unit_name ?? u.name,
      fuzzy: true,
    });
    setUnitMatches(matches);
    setFuzzyMatchedUnitIds(new Set(fuzzyIds));
    if (showToast) {
      const fuzzySuffix = fuzzyIds.length > 0 ? ` (${fuzzyIds.length} fuzzy, please verify)` : '';
      toast.info(`Auto-matched ${count} of ${total} units${fuzzySuffix}`, { closeButton: true });
    }
  }

  const handleFile = (file: File) => {
    setParsing(true);
    setFileName(file.name);
    setParsedRows([]);
    setSelectedPropertyName(null);
    setUnitMatches({});

    const ext = file.name.toLowerCase().split('.').pop() ?? '';

    const finish = (raw: Record<string, unknown>[]) => {
      const { rows, missingHeaders } = parseAppfolioRows(raw);
      if (missingHeaders.length > 0) {
        toast.error(`File is missing required columns: ${missingHeaders.join(', ')}`, {
          closeButton: true,
        });
        setParsedRows([]);
        setFileName('');
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else if (rows.length === 0) {
        toast.error('No valid unit rows found in file', { closeButton: true });
        setParsedRows([]);
        setFileName('');
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        setParsedRows(rows);
      }
      setParsing(false);
    };

    if (ext === 'csv') {
      Papa.parse<Record<string, unknown>>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => finish(results.data ?? []),
        error: () => {
          toast.error('Failed to parse CSV file', { closeButton: true });
          setParsing(false);
          setFileName('');
          if (fileInputRef.current) fileInputRef.current.value = '';
        },
      });
    } else if (ext === 'xlsx' || ext === 'xls') {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target!.result as ArrayBuffer);
          const wb = XLSX.read(data, { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '' });
          finish(json);
        } catch {
          toast.error('Failed to parse Excel file', { closeButton: true });
          setParsing(false);
          setFileName('');
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      toast.error('Please upload a .xlsx or .csv file', { closeButton: true });
      setParsing(false);
      setFileName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!selectedPropertyName) return;
    setIsSaving(true);
    try {
      const config = buildAppfolioStructureConfig(selectedPropertyName);
      await onUpdate('APPFOLIO' as PmsType, config);

      const savePromises: Promise<void>[] = [];
      for (const unit of billableUnits) {
        const match = unitMatches[unit.id];
        if (match) {
          // TODO: occupancy_uid / occupancy_id / tenant_name go stale on tenant turnover.
          // SimpleSub does not yet track formal lease move-in/move-out events (only
          // subscribers have move dates). When that lands, hook a refresh here that
          // invalidates these fields, or even better, auto-refreshes by re-fetching the
          // AppFolio template per-property on a schedule. Until then, the user must
          // re-upload manually via Reconfigure AppFolio after turnover, and the
          // AppfolioConfigForm surfaces a warning to that effect.
          savePromises.push(
            updateUnitPmsConfig(unit.id, 'APPFOLIO', buildAppfolioUnitConfig(match)),
          );
        } else if (unit.pms_config) {
          savePromises.push(updateUnitPmsConfig(unit.id, null, null));
        }
      }
      if (savePromises.length > 0) {
        await Promise.all(savePromises);
      }

      onRefresh?.();
      toast.success('PMS settings saved successfully', { closeButton: true });
      onClose();
      onSaveSuccess?.();
    } catch {
      toast.error('Failed to save PMS settings', { closeButton: true });
    } finally {
      setIsSaving(false);
    }
  };

  const matchedCount = billableUnits.filter((u) => unitMatches[u.id]).length;
  const canSave = !isSaving && !!selectedPropertyName && matchedCount > 0;

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} fullWidth maxWidth="md">
      <DialogTitle>
        Match Units via AppFolio Export
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={isSaving}
          size="small"
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Step 0: file upload */}
        <Box sx={{ mb: 3, mt: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Upload AppFolio Charges Import Template
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            In AppFolio, search <strong>&quot;Bulk Charges and Credits&quot;</strong>, scroll to the{' '}
            <strong>Format and Download Template</strong> section, pick the relevant properties, and
            click <strong>Download Template</strong>. Then upload the resulting file here.
            We&apos;ll read the Property Name, Unit ID, and Unit Name columns to pre-fill the
            mappings below.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              size="small"
              component="label"
              disabled={parsing || isSaving}
            >
              Choose File
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }}
              />
            </Button>
            {parsing && <CircularProgress size={20} />}
            {!parsing && fileName && (
              <Typography variant="body2" color="textSecondary">
                {fileName}: {parsedRows.length} unit row{parsedRows.length !== 1 ? 's' : ''} parsed
              </Typography>
            )}
          </Box>
        </Box>

        {/* Step 1: property selection */}
        {parsedRows.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Select Property
            </Typography>
            {distinctPropertyNames.length === 1 ? (
              <Typography variant="body2">
                <strong>{distinctPropertyNames[0]}</strong>{' '}
                <Typography component="span" variant="body2" color="textSecondary">
                  (only property in this file)
                </Typography>
              </Typography>
            ) : (
              <Autocomplete
                options={distinctPropertyNames}
                value={selectedPropertyName}
                onChange={(_e, val) => setSelectedPropertyName(val)}
                renderInput={(params) => (
                  <TextField {...params} size="small" placeholder="Pick the property..." />
                )}
              />
            )}
          </Box>
        )}

        {/* Step 2: unit mapping */}
        {selectedPropertyName && rowsForSelectedProperty.length > 0 && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                Unit Mappings ({matchedCount} of {billableUnits.length} matched)
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => autoMatch(rowsForSelectedProperty, true)}
              >
                Auto-Match
              </Button>
            </Box>
            <TableContainer sx={{ maxHeight: 350, mb: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>SimpleSub Unit</TableCell>
                    <TableCell>AppFolio Unit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {billableUnits.map((unit) => {
                    const currentMatch = unitMatches[unit.id] ?? null;
                    const isFuzzyMatch = fuzzyMatchedUnitIds.has(unit.id);
                    const availableOptions = rowsForSelectedProperty.filter(
                      (r) => !selectedRowIds.has(r.unitId) || r.unitId === currentMatch?.unitId,
                    );
                    return (
                      <TableRow key={unit.id}>
                        <TableCell>
                          <Typography variant="body2">{unit.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ flexGrow: 1 }}>
                              <Autocomplete
                                size="small"
                                options={availableOptions}
                                getOptionLabel={(opt) => {
                                  const base = opt.unitName || opt.unitId;
                                  return opt.tenantName ? `${base} (${opt.tenantName})` : base;
                                }}
                                value={currentMatch}
                                onChange={(_e, val) => {
                                  setUnitMatches((prev) => ({ ...prev, [unit.id]: val }));
                                  if (fuzzyMatchedUnitIds.has(unit.id)) {
                                    setFuzzyMatchedUnitIds((prev) => {
                                      const next = new Set(prev);
                                      next.delete(unit.id);
                                      return next;
                                    });
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} placeholder="Select AppFolio unit..." />
                                )}
                                isOptionEqualToValue={(opt, val) => opt.unitId === val.unitId}
                              />
                            </Box>
                            {isFuzzyMatch && (
                              <Tooltip
                                title="Auto-matched after normalizing punctuation and whitespace. Double-check this is the right unit."
                                arrow
                              >
                                <Chip
                                  label="Fuzzy"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <SWLoadingButtonOutlined onClick={onClose} disabled={isSaving}>
          Cancel
        </SWLoadingButtonOutlined>
        <SWLoadingButton onClick={handleSave} loading={isSaving} disabled={!canSave}>
          Save Mappings
        </SWLoadingButton>
      </DialogActions>
    </Dialog>
  );
};
